import { Injectable } from '@angular/core';
import { poe1 } from '../../game';
import { EndgameContent } from '../models/enums';
import { CustomRule, CustomRuleGroup, FavouriteCurrencyType, Filter } from '../models/filter-models';
import { PricedItemWithImage, TieredUnique } from '../models/item-data-models';
import { ENDGAME_PRESET_CUSTOM_RULES, EndgamePresetCustomRuleDef } from '../data/poe2/endgame-preset-custom-rules';
import { ENDGAME_PRESET_FAVOURITE_CURRENCIES } from '../data/endgame-preset-favourite-currencies';
import { getDefaultCustomRuleGroup, renumberRulesInGroup } from '../common/custom-rules/custom-rule-groups-helper';
import { ItemDataService } from './item-data.service';
import { CurrencyTierService } from './tiering/currency-tier.service';
import { UniquesTierService } from './tiering/uniques-tier.service';

export interface BossOnlyLowValueBossUnique {
  baseType: string;
  isFavourite: boolean;
  bossDropContent?: EndgameContent;
}

@Injectable({
  providedIn: 'root',
})
export class EndgameContentPresetService {
  constructor(
    private readonly itemDataService: ItemDataService,
    private readonly currencyTierService: CurrencyTierService,
    private readonly uniquesTierService: UniquesTierService,
  ) {}

  sync(filter: Filter): void {
    this.syncFavouriteCurrency(filter);
    this.syncFavouriteScarabs(filter);
    this.syncCustomRules(filter);
    this.syncPoe1HeistFlags(filter);
    this.currencyTierService.combineCurrencyTierListData(filter);
  }

  getSelectedEndgameContent(filter: Filter): EndgameContent[] {
    if (filter.favouriteEndgameContent.includes('All')) return [];
    return filter.favouriteEndgameContent.filter((c): c is EndgameContent => c !== 'All');
  }

  hasFavouriteCurrencyEffectForContent(content: EndgameContent): boolean {
    if (ENDGAME_PRESET_FAVOURITE_CURRENCIES.some(d => d.content === content)) {
      return true;
    }

    const perContentItemTypes = new Set(
      ENDGAME_PRESET_FAVOURITE_CURRENCIES.filter(d => !d.aggregate).flatMap(d => d.itemTypes)
    );
    return this.itemDataService.getFavouriteCurrencyItemTypesByContent(content)
      .some(type => !perContentItemTypes.has(type));
  }

  hasCustomRulesEffectForContent(content: EndgameContent): boolean {
    return ENDGAME_PRESET_CUSTOM_RULES.some(d => d.content === content);
  }

  selectionHasFavouriteCurrencyEffect(filter: Filter): boolean {
    return this.getSelectedEndgameContent(filter)
      .some(content => this.hasFavouriteCurrencyEffectForContent(content));
  }

  selectionHasScarabEffect(filter: Filter): boolean {
    if (!poe1) return false;
    const scarabContents = new Set(this.itemDataService.getScarabContentTypes());
    return this.getSelectedEndgameContent(filter).some(content => scarabContents.has(content));
  }

  selectionHasCustomRulesEffect(filter: Filter): boolean {
    return this.getSelectedEndgameContent(filter)
      .some(content => this.hasCustomRulesEffectForContent(content));
  }

  getSelectedCustomRuleDescriptions(filter: Filter): { content: EndgameContent; description: string }[] {
    const selected = new Set(this.getSelectedEndgameContent(filter));
    const seen = new Set<EndgameContent>();
    const entries: { content: EndgameContent; description: string }[] = [];

    for (const def of ENDGAME_PRESET_CUSTOM_RULES) {
      if (!selected.has(def.content) || seen.has(def.content)) continue;
      seen.add(def.content);
      entries.push({ content: def.content, description: def.description });
    }

    return entries;
  }

  getFavouriteRestrictedBossBaseTypes(filter: Filter, restrictedItems: TieredUnique[]): string[] {
    if (!filter.showFavouriteRestrictedBossUniques) return [];
    if (filter.favouriteEndgameContent.includes('All')) return [];

    const selectedContent = new Set(this.getSelectedEndgameContent(filter));
    const highValueThreshold = this.uniquesTierService.getHighValueThreshold(filter);

    return restrictedItems
      .map(item => ({
        item,
        topPrice: this.getValuableFavouriteContentUniques(item, selectedContent, highValueThreshold)[0]?.price || 0,
      }))
      .filter(({ topPrice }) => topPrice > 0)
      .sort((a, b) => b.topPrice - a.topPrice)
      .map(({ item }) => item.baseType);
  }

  getFavouriteContentDisplayUnique(filter: Filter, item: TieredUnique): PricedItemWithImage | undefined {
    if (filter.favouriteEndgameContent.includes('All')) return undefined;

    const selectedContent = new Set(this.getSelectedEndgameContent(filter));
    const highValueThreshold = this.uniquesTierService.getHighValueThreshold(filter);

    return this.getValuableFavouriteContentUniques(item, selectedContent, highValueThreshold)[0];
  }

  getBossOnlyLowValueBossUniques(filter: Filter, otherItems: TieredUnique[]): BossOnlyLowValueBossUnique[] {
    const hasSpecificFavouriteEndgameContent = !filter.favouriteEndgameContent.includes('All');
    const selectedContent = new Set(this.getSelectedEndgameContent(filter));

    return otherItems
      .filter(item =>
        item.allUniques.length > 0 &&
        item.allUniques.every(u => !!u.bossDrop)
      )
      .map(item => {
        const bossDropContent = item.allUniques.find(u => u.bossDropContent)?.bossDropContent;
        return {
          baseType: item.baseType,
          isFavourite: hasSpecificFavouriteEndgameContent
            && item.allUniques.some(u => u.bossDropContent && selectedContent.has(u.bossDropContent)),
          bossDropContent,
        };
      });
  }

  getFavouriteLowValueBossBaseTypes(filter: Filter, otherItems: TieredUnique[]): string[] {
    if (!filter.showFavouriteLowValueBossUniques) return [];

    return this.getBossOnlyLowValueBossUniques(filter, otherItems)
      .filter(x => x.isFavourite)
      .map(x => x.baseType);
  }

  getOtherLowValueBossBaseTypes(filter: Filter, otherItems: TieredUnique[]): string[] {
    if (!filter.showOtherLowValueBossUniques) return [];

    return this.getBossOnlyLowValueBossUniques(filter, otherItems)
      .filter(x => !x.isFavourite)
      .map(x => x.baseType);
  }

  private isBossDropRestricted(unique: PricedItemWithImage): boolean {
    const restrictedDropTypes = this.itemDataService.getRestrictedDropTypesToTreatAsRegular();
    return !!unique.bossDrop && !restrictedDropTypes.includes(unique.bossDrop);
  }

  private getValuableFavouriteContentUniques(
    item: TieredUnique,
    selectedContent: ReadonlySet<EndgameContent>,
    highValueThreshold: number,
  ): PricedItemWithImage[] {
    return item.allUniques
      .filter(u =>
        u.bossDropContent &&
        selectedContent.has(u.bossDropContent) &&
        this.isBossDropRestricted(u) &&
        (u.price || 0) >= highValueThreshold,
      )
      .sort((a, b) => (b.price || 0) - (a.price || 0));
  }

  private syncFavouriteCurrency(filter: Filter): void {
    filter.favouriteCurrencyTypes = filter.favouriteCurrencyTypes.filter(e => !e.fromPresets);

    if (filter.favouriteEndgameContent.includes('All') || !filter.endgamePresetPromoteFavouriteCurrency) {
      return;
    }

    const selected = filter.favouriteEndgameContent.filter((c): c is EndgameContent => c !== 'All');
    const selectedSet = new Set(selected);

    const perContentDefs = ENDGAME_PRESET_FAVOURITE_CURRENCIES.filter(d => !d.aggregate);
    const perContentItemTypes = new Set(perContentDefs.flatMap(d => d.itemTypes));

    for (const def of perContentDefs) {
      if (!def.content || !selectedSet.has(def.content)) continue;

      this.insertPresetFavouriteCurrencyEntry(filter, new FavouriteCurrencyType({
        fromPresets: true,
        fromPresetsEndgameContent: def.content,
        enabled: true,
        itemTypes: [...def.itemTypes],
        mode: def.mode,
        value: def.value,
      }));
    }

    const aggregateDef = ENDGAME_PRESET_FAVOURITE_CURRENCIES.find(d => d.aggregate);
    if (aggregateDef) {
      const itemTypes = [...new Set(
        selected.flatMap(content =>
          this.itemDataService.getFavouriteCurrencyItemTypesByContent(content)
            .filter(type => !perContentItemTypes.has(type))
        )
      )].sort();

      if (itemTypes.length > 0) {
        this.insertPresetFavouriteCurrencyEntry(filter, new FavouriteCurrencyType({
          fromPresets: true,
          enabled: true,
          itemTypes,
          mode: aggregateDef.mode,
          value: aggregateDef.value,
        }));
      }
    }
  }

  private syncFavouriteScarabs(filter: Filter): void {
    const fromPresets = new Set(filter.favouriteScarabsTypesFromPresets);
    filter.favouriteScarabsTypes = filter.favouriteScarabsTypes.filter(c => !fromPresets.has(c as EndgameContent));
    if (filter.favouriteScarabsTypes.length === 0) {
      filter.favouriteScarabsTypes = ['None'];
    }
    filter.favouriteScarabsTypesFromPresets = [];

    if (!poe1 || filter.favouriteEndgameContent.includes('All') || !filter.endgamePresetPromoteScarabs) {
      return;
    }

    const scarabContents = new Set(this.itemDataService.getScarabContentTypes());
    const toAdd = this.getSelectedEndgameContent(filter).filter(c => scarabContents.has(c));
    if (toAdd.length === 0) return;

    filter.favouriteScarabs = true;
    const manual = filter.favouriteScarabsTypes.filter((c): c is EndgameContent => c !== 'None');
    // Only tag as fromPresets what was not already a manual selection, so promote-off keeps manual picks
    filter.favouriteScarabsTypesFromPresets = toAdd.filter(c => !manual.includes(c));
    filter.favouriteScarabsTypes = [...new Set([...manual, ...toAdd])];
  }

  private syncCustomRules(filter: Filter): void {
    if (filter.favouriteEndgameContent.includes('All')) {
      this.removeAllPresetRules(filter);
      return;
    }

    const selected = new Set(
      filter.favouriteEndgameContent.filter((c): c is EndgameContent => c !== 'All')
    );

    this.syncRuleGroups(filter.customRuleGroups, selected, false, filter.endgamePresetEnableCustomRules);
    this.syncRuleGroups(filter.customCosmeticRuleGroups, selected, true, filter.endgamePresetEnableCustomRules);
  }

  private syncPoe1HeistFlags(filter: Filter): void {
    if (!poe1 || filter.favouriteEndgameContent.includes('All')) {
      return;
    }

    const selected = new Set(
      filter.favouriteEndgameContent.filter((c): c is EndgameContent => c !== 'All')
    );
    const heistSelected = selected.has(EndgameContent.Heist);

    filter.showHeistEnchantedBlueprints = heistSelected;
    filter.showHeistEnchantedContracts = heistSelected;
    filter.showHeistBlueprints = heistSelected;
    filter.showHeistContracts = heistSelected;
    filter.showRoguesMarkers = heistSelected;
  }

  private syncRuleGroups(
    groups: CustomRuleGroup[],
    selected: Set<EndgameContent>,
    cosmetic: boolean,
    enableCustomRules: boolean,
  ): void {
    const defaultGroup = getDefaultCustomRuleGroup(groups);
    const presetDefs = ENDGAME_PRESET_CUSTOM_RULES.filter(d => d.cosmetic === cosmetic);

    defaultGroup.rules = defaultGroup.rules.filter(r => {
      if (!r.fromPresets) return true;
      if (!enableCustomRules) return false;
      return r.fromPresetsEndgameContent !== undefined && selected.has(r.fromPresetsEndgameContent);
    });

    if (!enableCustomRules) {
      renumberRulesInGroup(defaultGroup);
      return;
    }

    for (const content of selected) {
      const defs = presetDefs.filter(d => d.content === content);
      if (defs.length === 0) continue;

      const existingCount = defaultGroup.rules.filter(
        r => r.fromPresets && r.fromPresetsEndgameContent === content
      ).length;

      if (existingCount === defs.length) continue;

      defaultGroup.rules = defaultGroup.rules.filter(
        r => !(r.fromPresets && r.fromPresetsEndgameContent === content)
      );

      for (const def of defs) {
        this.insertPresetRule(defaultGroup, this.createPresetRule(def));
      }
    }

    this.reorderPresetRulesFirst(defaultGroup, presetDefs);
    renumberRulesInGroup(defaultGroup);
  }

  private removeAllPresetRules(filter: Filter): void {
    for (const groups of [filter.customRuleGroups, filter.customCosmeticRuleGroups]) {
      const defaultGroup = getDefaultCustomRuleGroup(groups);
      defaultGroup.rules = defaultGroup.rules.filter(r => !r.fromPresets);
      renumberRulesInGroup(defaultGroup);
    }
  }

  private createPresetRule(def: EndgamePresetCustomRuleDef): CustomRule {
    return new CustomRule(structuredClone({
      ...def.rule,
      fromPresets: true,
      fromPresetsEndgameContent: def.content,
    }));
  }

  private insertPresetRule(group: CustomRuleGroup, rule: CustomRule): void {
    const firstManualIndex = group.rules.findIndex(r => !r.fromPresets);
    if (firstManualIndex >= 0) {
      group.rules.splice(firstManualIndex, 0, rule);
    } else {
      group.rules.push(rule);
    }
  }

  private reorderPresetRulesFirst(group: CustomRuleGroup, presetDefs: EndgamePresetCustomRuleDef[]): void {
    const manual = group.rules.filter(r => !r.fromPresets);
    const preset = group.rules.filter(r => r.fromPresets);
    const contentOrder = [...new Set(presetDefs.map(d => d.content))];

    preset.sort((a, b) => contentOrder.indexOf(a.fromPresetsEndgameContent!) - contentOrder.indexOf(b.fromPresetsEndgameContent!));
    group.rules = [...preset, ...manual];
  }

  private insertPresetFavouriteCurrencyEntry(filter: Filter, entry: FavouriteCurrencyType): void {
    const firstManualIndex = filter.favouriteCurrencyTypes.findIndex(e => !e.fromPresets);
    if (firstManualIndex >= 0) {
      filter.favouriteCurrencyTypes.splice(firstManualIndex, 0, entry);
    } else {
      filter.favouriteCurrencyTypes.push(entry);
    }
  }
}
